import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivateFn } from '@angular/router';
import { Observable, filter, map, take } from 'rxjs';
import { StateService } from '../services/state.service';

/**
 * Resolves whether the current user is authenticated.
 *
 * With cookie-based auth, there is no client-readable token to check synchronously.
 * The app initializer calls GET /users/current on startup and populates StateService.
 * This guard always waits for that initialization to complete, then checks for a user.
 */
function resolveAuth(
  router: Router,
  stateService: StateService,
  returnUrl: string,
): Observable<boolean> | boolean {
  if (stateService.isInitialized()) {
    const authed = !!stateService.getCurrentUser();
    if (!authed) router.navigate(['/login'], { queryParams: { returnUrl } });
    return authed;
  }

  // AppInitializer is still in-flight — wait for it to complete
  return stateService.isInitialized$.pipe(
    filter(v => v),
    take(1),
    map(() => {
      const authed = !!stateService.getCurrentUser();
      if (!authed) router.navigate(['/login'], { queryParams: { returnUrl } });
      return authed;
    }),
  );
}

export function authGuard(): CanActivateFn {
  return (_route, state) => {
    return resolveAuth(
      inject(Router),
      inject(StateService),
      state.url,
    );
  };
}

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  constructor(
    private router: Router,
    private stateService: StateService,
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return resolveAuth(this.router, this.stateService, state.url);
  }
}
